import { memo, ReactNode, useCallback, useState } from "react";
import { Icon } from "@iconify/react";
import { Colors } from "../../utils/colors";
import { FoldHeaderText } from "./SubComponents/FoldHeaderText";
import LabelButton from "../Buttons/LabelButton";
import RectangleButton from "../Buttons/RectangleButton";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import { DropdownMenu, DropdownMenuItem } from "../DropdownMenu";
import MarkitLogoBlue from "../../assets/MarkitLogoBlue.png";
import { MARKIT_DOCS } from "@markit/common.utils";

type MoreThanJustTextingFoldProps = {
  isDesktopSize: boolean;
};

const cardGlow = (width: number, height: number, color: string) => (
  <div
    style={{
      position: "absolute",
      width,
      height,
      top: -height / 4,
      left: "50%",
      transform: "translateX(-50%)",
      backgroundImage: `radial-gradient(ellipse at center, ${color} 0%, transparent 70%)`,
      filter: "blur(30px)",
      pointerEvents: "none",
    }}
  />
);

const learnMoreButton = (onPress: () => void) => (
  <RectangleButton
    buttonLabel="Learn More"
    onPress={onPress}
    altColor={Colors.WHITE}
    altTextColor={Colors.BLACK}
    iconRight={
      <Icon
        icon="ion:chevron-forward"
        height={14}
        style={{ color: Colors.BLACK }}
      />
    }
    containerStyles={{
      padding: "12px 16px",
      borderRadius: 10,
      width: "fit-content",
    }}
  />
);

// Card 01 — Host a newsletter or blog
const NewsletterMockup = () => (
  <div
    className="ColumnNormal"
    style={{
      gap: 10.5,
      padding: 21,
      borderRadius: 24.6,
      border: "1px solid rgba(255,255,255,0.14)",
      backgroundColor: Colors.BLACK,
      boxShadow: "0 10px 15px rgba(0,0,0,0.5)",
      width: "100%",
      boxSizing: "border-box",
    }}
  >
    <span style={{ color: Colors.WHITE, fontSize: 17, fontWeight: 700 }}>
      Markit Newsletter (April Week 5, 2026)
    </span>
    <span style={{ color: "#808087", fontSize: 12, lineHeight: "16px" }}>
      This week we added file upload support and an &quot;Other&quot; option to
      your questions.
    </span>
    <div className="AlignedRow" style={{ gap: 7 }}>
      <img src={MarkitLogoBlue} alt="Markit" style={{ width: 14, height: 9 }} />
      <span
        style={{
          color: "#d9d9e0",
          fontSize: 12,
          textDecoration: "underline",
        }}
      >
        By Markit AI
      </span>
      <div style={{ flex: 1 }} />
      <span style={{ color: "#8c8c94", fontSize: 11 }}>Apr. 30th, 2:12 PM</span>
    </div>
    <HorizontalDivider
      altMargin={0}
      altColor="rgba(255,255,255,0.14)"
      altWidth={0.5}
    />
    <div className="AlignedRow" style={{ gap: 8 }}>
      <div
        style={{
          border: "0.5px solid rgba(255,255,255,0.25)",
          borderRadius: 100,
          padding: "8px 16px",
        }}
      >
        <span style={{ color: Colors.WHITE, fontSize: 11 }}>Subscribe</span>
      </div>
      <div style={{ flex: 1 }} />
      <div
        style={{
          border: "0.5px solid rgba(255,255,255,0.25)",
          borderRadius: 100,
          padding: "8px 16px",
        }}
      >
        <span style={{ color: Colors.WHITE, fontSize: 11 }}>Share</span>
      </div>
    </div>
    <span style={{ color: Colors.WHITE, fontSize: 12, fontWeight: 700 }}>
      All past newsletter updates ➡{" "}
      <span style={{ color: Colors.BLUE5, textDecoration: "underline" }}>
        Link to Newsletters
      </span>
    </span>
    <span style={{ color: "#d9d9e0", fontSize: 12, lineHeight: "17px" }}>
      I&apos;m Peter Chang, and I lead the software team at Markit AI. I write
      this weekly newsletter to give you a direct look at what we&apos;re
      building.
    </span>
  </div>
);

// Card 02 — Import existing contacts
const ImportContactsMockup = () => (
  <div
    className="ColumnNormal"
    style={{
      gap: 9,
      padding: 15,
      borderRadius: 24,
      border: "1px solid rgba(255,255,255,0.14)",
      backgroundColor: Colors.BLACK,
      boxShadow: "0 10px 15px rgba(0,0,0,0.5)",
      width: "100%",
      boxSizing: "border-box",
    }}
  >
    <div
      className="AlignedRow"
      style={{
        gap: 9,
        padding: "9px 11px",
        borderRadius: 9,
        backgroundColor: "#131314",
      }}
    >
      <span style={{ color: Colors.WHITE, fontSize: 12, fontWeight: 600 }}>
        Import Contacts
      </span>
      <div style={{ flex: 1 }} />
      <span style={{ color: Colors.GRAY1, fontSize: 13 }}>›</span>
    </div>
    <div
      className="AlignedRow"
      style={{
        gap: 7,
        padding: "7px 11px",
        borderRadius: 9,
        backgroundColor: "#131314",
      }}
    >
      <span style={{ color: Colors.WHITE, fontSize: 10 }}>contacts.csv</span>
      <span style={{ color: Colors.GRAY1, fontSize: 10 }}>
        · 2,431 imported
      </span>
      <div style={{ flex: 1 }} />
      <div
        className="ColumnCentering"
        style={{
          width: 13,
          height: 13,
          borderRadius: "50%",
          backgroundColor: Colors.GREEN2,
        }}
      >
        <Icon icon="ion:checkmark" height={9} style={{ color: Colors.WHITE }} />
      </div>
    </div>
    <div
      className="ColumnNormal"
      style={{
        gap: 5,
        padding: 11,
        borderRadius: 9,
        backgroundColor: "#131314",
      }}
    >
      <span style={{ color: Colors.GRAY1, fontSize: 10 }}>Your Audience</span>
      <div className="AlignedRow" style={{ gap: 6 }}>
        <span style={{ color: Colors.WHITE, fontSize: 17, fontWeight: 600 }}>
          2,781
        </span>
        <span style={{ color: Colors.GREEN2, fontSize: 10 }}>+4.6%</span>
      </div>
      <span style={{ color: Colors.GRAY1, fontSize: 9 }}>New Followers</span>
      <svg
        viewBox="0 0 232 33"
        width="100%"
        height={33}
        style={{ marginTop: 4 }}
      >
        <polyline
          points="0,26 40,20 80,24 120,10 160,15 200,4 232,8"
          fill="none"
          stroke={Colors.BLUE5}
          strokeWidth={2}
        />
      </svg>
    </div>
  </div>
);

// Card 03 — Collect contact info & signups
const SignupFormMockup = () => (
  <div
    className="ColumnNormal"
    style={{
      gap: 6,
      padding: 12,
      borderRadius: 20,
      border: "1px solid rgba(255,255,255,0.14)",
      backgroundColor: Colors.BLACK,
      boxShadow: "0 10px 15px rgba(0,0,0,0.5)",
      width: "100%",
      boxSizing: "border-box",
    }}
  >
    <span style={{ color: Colors.WHITE, fontSize: 11, fontWeight: 600 }}>
      Want to Attend a Creators x Founders Event?
    </span>
    <span style={{ color: "#d9d9de", fontSize: 9 }}>
      Sign up if you want to apply to join one of our events
    </span>
    <HorizontalDivider
      altMargin={0}
      altColor="rgba(255,255,255,0.16)"
      altWidth={0.5}
    />
    {["Your name", "Phone number"].map((placeholder) => (
      <div
        key={placeholder}
        style={{
          padding: "7px 14px",
          borderRadius: 100,
          border: "0.5px solid rgba(255,255,255,0.14)",
          backgroundColor: "#131314",
        }}
      >
        <span style={{ color: "#6b6b70", fontSize: 10 }}>{placeholder}</span>
      </div>
    ))}
    <div
      className="ColumnCentering"
      style={{
        padding: "8px 0",
        borderRadius: 100,
        backgroundColor: Colors.WHITE,
      }}
    >
      <span style={{ color: Colors.BLACK, fontSize: 10, fontWeight: 600 }}>
        Verify Number
      </span>
    </div>
    <HorizontalDivider
      altMargin={0}
      altColor="rgba(255,255,255,0.16)"
      altWidth={0.5}
    />
    <span style={{ color: "#737378", fontSize: 11, fontWeight: 600 }}>
      Questions
    </span>
    <span style={{ color: "#59595e", fontSize: 9 }}>
      Available after info entered
    </span>
    <span style={{ color: "#737378", fontSize: 10, fontWeight: 600 }}>
      Enter a valid email *
    </span>
    <div
      style={{
        padding: "7px 14px",
        borderRadius: 100,
        border: "0.5px solid rgba(255,255,255,0.14)",
        backgroundColor: "#131314",
      }}
    >
      <span style={{ color: "#6b6b70", fontSize: 10 }}>
        Enter your email...
      </span>
    </div>
  </div>
);

// Card 04 — Sell digital products
const DigitalProductMockup = () => (
  <div
    className="ColumnNormal"
    style={{
      gap: 14,
      padding: 18,
      borderRadius: 20,
      border: "1px solid rgba(255,255,255,0.14)",
      backgroundColor: Colors.BLACK,
      boxShadow: "0 10px 15px rgba(0,0,0,0.5)",
      width: "100%",
      boxSizing: "border-box",
    }}
  >
    <span
      style={{
        color: Colors.WHITE,
        fontSize: 16,
        fontWeight: 700,
        lineHeight: "20px",
      }}
    >
      Boston Startup Ecosystem
      <br />
      Deep Dive Call (60 min)
    </span>
    <div
      className="ColumnNormal"
      style={{
        gap: 9,
        padding: 10,
        borderRadius: 13,
        border: "0.5px solid rgba(255,255,255,0.14)",
        backgroundColor: "#131314",
      }}
    >
      <div
        className="AlignedRow"
        style={{
          gap: 10,
          padding: 11,
          borderRadius: 10,
          border: "0.5px solid rgba(255,255,255,0.14)",
          backgroundColor: "#131314",
        }}
      >
        <div className="ColumnNormal" style={{ gap: 2, flex: 1 }}>
          <span style={{ color: Colors.WHITE, fontSize: 9, fontWeight: 600 }}>
            Order
          </span>
          <span style={{ color: Colors.WHITE, fontSize: 12 }}>$200.00</span>
          <span style={{ color: "#808087", fontSize: 8 }}>
            I will reach out to schedule time with you after purchase
          </span>
        </div>
        <div
          className="ColumnCentering"
          style={{
            width: 17,
            height: 17,
            borderRadius: "50%",
            backgroundColor: Colors.WHITE,
          }}
        >
          <Icon
            icon="ion:checkmark"
            height={10}
            style={{ color: Colors.BLACK }}
          />
        </div>
      </div>
      <div
        className="ColumnCentering"
        style={{
          padding: "10px 0",
          borderRadius: 10,
          backgroundColor: Colors.WHITE,
          width: "100%",
        }}
      >
        <span style={{ color: Colors.BLACK, fontSize: 11, fontWeight: 700 }}>
          Buy Now
        </span>
      </div>
    </div>
  </div>
);

// Card 05 — Organize events / sell tickets
const EventMockup = () => (
  <div
    className="ColumnNormal"
    style={{
      gap: 10,
      padding: 18,
      borderRadius: 20,
      border: "1px solid rgba(255,255,255,0.14)",
      backgroundColor: Colors.BLACK,
      boxShadow: "0 10px 15px rgba(0,0,0,0.5)",
      width: "100%",
      boxSizing: "border-box",
    }}
  >
    <span
      style={{
        color: Colors.WHITE,
        fontSize: 14,
        fontWeight: 700,
        lineHeight: "18px",
        whiteSpace: "nowrap",
      }}
    >
      Creators x Founders at Remedy Place
    </span>
    <div className="AlignedRow" style={{ gap: 9 }}>
      <div
        className="ColumnCentering"
        style={{
          width: 34,
          height: 34,
          borderRadius: 9,
          border: "0.5px solid rgba(255,255,255,0.1)",
          backgroundColor: "#1c1c1f",
        }}
      >
        <span style={{ color: "#9e9ea6", fontSize: 8 }}>Jan</span>
        <span style={{ color: "#fe296c", fontSize: 12, fontWeight: 600 }}>
          16
        </span>
      </div>
      <div className="ColumnNormal" style={{ gap: 2 }}>
        <span style={{ color: Colors.WHITE, fontSize: 10, fontWeight: 600 }}>
          Friday, Jan. 16
        </span>
        <span style={{ color: "#9e9ea6", fontSize: 9 }}>
          6:00 PM to 8:00 PM EDT
        </span>
      </div>
    </div>
    <div className="AlignedRow" style={{ gap: 9 }}>
      <div
        className="ColumnCentering"
        style={{
          width: 34,
          height: 34,
          borderRadius: 9,
          border: "0.5px solid rgba(255,255,255,0.1)",
          backgroundColor: "#1c1c1f",
        }}
      >
        <Icon icon="ion:location" height={16} style={{ color: Colors.BLUE5 }} />
      </div>
      <div className="ColumnNormal" style={{ gap: 2 }}>
        <span style={{ color: Colors.WHITE, fontSize: 10, fontWeight: 600 }}>
          Remedy Place Boston
        </span>
        <span style={{ color: "#9e9ea6", fontSize: 9 }}>
          370 Summer St, Boston, MA 02210, USA ↗
        </span>
      </div>
    </div>
    <div
      className="ColumnNormal"
      style={{
        gap: 8,
        padding: 9,
        borderRadius: 13,
        border: "0.5px solid rgba(255,255,255,0.12)",
        backgroundColor: "#131314",
      }}
    >
      <div
        className="AlignedRow"
        style={{
          gap: 9,
          padding: 10,
          borderRadius: 9,
          border: "0.5px solid rgba(255,255,255,0.16)",
        }}
      >
        <div className="ColumnNormal" style={{ gap: 3, flex: 1 }}>
          <span style={{ color: Colors.WHITE, fontSize: 10, fontWeight: 600 }}>
            RSVP
          </span>
          <span style={{ color: "#9e9ea6", fontSize: 9 }}>Free</span>
        </div>
        <div
          style={{
            padding: "3px 8px",
            borderRadius: 100,
            backgroundColor: Colors.GREEN2 + "29",
          }}
        >
          <span
            style={{
              color: Colors.GREEN2,
              fontSize: 8,
              fontWeight: 600,
              lineHeight: "10px",
              display: "block",
            }}
          >
            24 spots left
          </span>
        </div>
      </div>
      <div
        className="AlignedRow"
        style={{
          gap: 9,
          padding: 10,
          borderRadius: 9,
          border: "0.5px solid rgba(255,255,255,0.16)",
        }}
      >
        <div className="ColumnNormal" style={{ gap: 3, flex: 1 }}>
          <span style={{ color: Colors.WHITE, fontSize: 10, fontWeight: 600 }}>
            VIP Ticket
          </span>
          <span style={{ color: "#9e9ea6", fontSize: 9 }}>$25.00</span>
        </div>
      </div>
    </div>
  </div>
);

// Card 06 — Start a paid community
const MembershipTiersMockup = (props: { isDesktopSize: boolean }) => {
  const { isDesktopSize } = props;

  const plan = (
    title: string,
    price: ReactNode,
    benefits: string,
    highlighted: boolean
  ) => (
    <div
      className="ColumnNormal"
      style={{
        gap: 10,
        padding: 16,
        borderRadius: 16,
        border: highlighted
          ? "1.5px solid rgba(255,255,255,0.85)"
          : "1px solid rgba(255,255,255,0.18)",
        flex: 1,
      }}
    >
      <span style={{ color: Colors.WHITE, fontSize: 15, fontWeight: 600 }}>
        {title}
      </span>
      <div className="AlignedRow" style={{ gap: 5 }}>
        {price}
      </div>
      <HorizontalDivider
        altMargin={0}
        altColor="rgba(255,255,255,0.14)"
        altWidth={0.5}
      />
      <span style={{ color: "#8c8c94", fontSize: 11 }}>Benefits</span>
      <span
        style={{ color: "#d1d1d9", fontSize: 12, lineHeight: "17px", flex: 1 }}
      >
        {benefits}
      </span>
      <div
        className="ColumnCentering"
        style={{
          padding: "12px 0",
          borderRadius: 12,
          border: highlighted ? "none" : "1px solid rgba(255,255,255,0.18)",
          backgroundColor: highlighted ? Colors.WHITE : "transparent",
          width: "100%",
        }}
      >
        <span
          style={{
            color: highlighted ? Colors.BLACK : Colors.WHITE,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          Select
        </span>
      </div>
    </div>
  );

  return (
    <div
      className="RowNormal"
      style={{
        gap: 12,
        padding: 20,
        borderRadius: 28,
        border: "1px solid rgba(255,255,255,0.14)",
        backgroundColor: Colors.BLACK,
        boxShadow: "0 12px 17px rgba(0,0,0,0.5)",
        width: isDesktopSize ? 512 : "100%",
        height: isDesktopSize ? 340 : undefined,
        boxSizing: "border-box",
      }}
    >
      {plan(
        "Free Plan",
        <span style={{ color: Colors.WHITE, fontSize: 15 }}>$0</span>,
        "A curated text every Monday with the best Boston startup and tech events.",
        false
      )}
      {plan(
        "Tech Insider",
        <>
          <span style={{ color: Colors.WHITE, fontSize: 15 }}>$7</span>
          <span style={{ color: "#8c8c94", fontSize: 12 }}>/ monthly</span>
        </>,
        "Invites to private events and free tickets. Plus a 30-min Zoom call, and text me questions anytime — I'll personally reply.",
        true
      )}
    </div>
  );
};

// Pixel-exact placement for the "absolute" layout cards (01, 02), lifted straight from Figma.
type AbsoluteLayout = {
  numberLeft: number;
  numberTop: number;
  titleLeft: number;
  titleTop: number;
  titleWidth: number;
  titleFontSize: number;
  titleLineHeight: number;
  titleLetterSpacing: string;
  subtextLeft: number;
  subtextTop: number;
  subtextWidth: number;
  subtextFontSize: number;
  subtextLineHeight: number;
  buttonLeft: number;
  buttonTop: number;
  mockupLeft: number;
  mockupTop: number;
  mockupWidth: number;
};

type UseCase = {
  number: string;
  title: string;
  subtext: string;
  cardHeight: number;
  rowShareFlex?: number; // flex-grow ratio when sharing a row with a sibling card (01, 02)
  glowColor: string;
  mockup: ReactNode;
  layout: "absolute" | "split" | "scrim";
  absoluteLayout?: AbsoluteLayout;
  background?: string;
  docsSlug: string;
};

const MoreThanJustTextingFold = (props: MoreThanJustTextingFoldProps) => {
  const { isDesktopSize } = props;
  const [selectedUseCase, setSelectedUseCase] = useState(0);

  const styles = {
    headerLabel: {
      fontSize: isDesktopSize ? 16 : 14,
      fontWeight: 500,
    },
  };

  const useCases: UseCase[] = [
    {
      number: "01",
      title: "Host a newsletter or blog",
      subtext:
        "Publish newsletter-style posts to your audience, without sending them to another site.",
      cardHeight: 440,
      rowShareFlex: 1.7,
      glowColor: "rgba(0,122,255,0.35)",
      mockup: <NewsletterMockup />,
      layout: "absolute",
      docsSlug: "host-a-newsletter-or-blog",
      absoluteLayout: {
        numberLeft: 35,
        numberTop: 35,
        titleLeft: 35,
        titleTop: 67,
        titleWidth: 300,
        titleFontSize: 34,
        titleLineHeight: 39,
        titleLetterSpacing: "-0.8px",
        subtextLeft: 35,
        subtextTop: 161,
        subtextWidth: 288,
        subtextFontSize: 16,
        subtextLineHeight: 24,
        buttonLeft: 35,
        buttonTop: 259,
        mockupLeft: 357,
        mockupTop: 117,
        mockupWidth: 450,
      },
    },
    {
      number: "02",
      title: "Import existing contacts",
      subtext:
        "Already have a list? Bring your contacts over from a spreadsheet, phone contacts, manual add, or API.",
      cardHeight: 440,
      rowShareFlex: 1,
      glowColor: "rgba(0,122,255,0.35)",
      mockup: <ImportContactsMockup />,
      layout: "absolute",
      docsSlug: "import-existing-contacts",
      absoluteLayout: {
        numberLeft: 31,
        numberTop: 31,
        titleLeft: 31,
        titleTop: 61,
        titleWidth: 340,
        titleFontSize: 26,
        titleLineHeight: 31,
        titleLetterSpacing: "-0.6px",
        subtextLeft: 31,
        subtextTop: 106,
        subtextWidth: 340,
        subtextFontSize: 15,
        subtextLineHeight: 22,
        buttonLeft: 31,
        buttonTop: 194,
        mockupLeft: 29,
        mockupTop: 267,
        mockupWidth: 430,
      },
    },
    {
      number: "03",
      title: "Collect contact info & signups",
      subtext:
        "Capture phone numbers, emails, and custom info with forms built to convert your audience.",
      cardHeight: 500,
      glowColor: "rgba(0,122,255,0.3)",
      mockup: <SignupFormMockup />,
      layout: "scrim",
      docsSlug: "collect-contact-info-and-signups",
    },
    {
      number: "04",
      title: "Sell digital products",
      subtext:
        "Sell courses, coaching, e-books, and exclusive content directly through your text list. No storefront needed.",
      cardHeight: 500,
      glowColor: "rgba(231,79,198,0.3)",
      mockup: <DigitalProductMockup />,
      layout: "scrim",
      docsSlug: "sell-digital-products",
    },
    {
      number: "05",
      title: "Organize events / sell tickets",
      subtext:
        "Create event pages, sell tickets, and send updates by text with third-party ticketing integrations built in.",
      cardHeight: 500,
      glowColor: "rgba(61,220,132,0.3)",
      mockup: <EventMockup />,
      layout: "scrim",
      docsSlug: "organize-events-sell-tickets",
    },
    {
      number: "06",
      title: "Start a paid community",
      subtext:
        "Charge monthly for exclusive access, content, and perks with paid membership tiers customers can join.",
      cardHeight: 360,
      glowColor: "rgba(0,122,255,0.4)",
      mockup: <MembershipTiersMockup isDesktopSize={isDesktopSize} />,
      layout: "split",
      background: "linear-gradient(90deg, #07080a 0%, #0f1420 100%)",
      docsSlug: "start-a-paid-community",
    },
  ];

  const learnMoreOnPress = useCallback((docsSlug: string) => {
    window.open(MARKIT_DOCS + "/" + docsSlug, "_blank");
  }, []);

  // Card with the mockup bleeding into the top, text scrimmed in from the bottom (cards 03-05)
  const renderScrimCard = (useCase: UseCase) => (
    <div
      key={useCase.number}
      className="ColumnNormal"
      style={{
        position: "relative",
        height: useCase.cardHeight,
        flex: isDesktopSize ? 1 : undefined,
        width: isDesktopSize ? undefined : "100%",
        borderRadius: 28,
        border: "1px solid rgba(255,255,255,0.1)",
        backgroundImage: "linear-gradient(135deg, #141519 0%, #07080a 71.4%)",
        boxShadow: "0 16px 40px rgba(0,0,0,0.45)",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {cardGlow(320, 190, useCase.glowColor)}
      <div style={{ padding: "20px 27px 0 27px", position: "relative" }}>
        {useCase.mockup}
      </div>
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: "56%",
          backgroundImage:
            "linear-gradient(to bottom, rgba(11,12,15,0) 0%, rgba(11,12,15,0.92) 20%, #0b0c0f 38%)",
        }}
      />
      <div
        className="ColumnNormal"
        style={{
          position: "relative",
          marginTop: "auto",
          padding: "0 27px 27px 27px",
          gap: 12,
        }}
      >
        <span
          style={{
            color: Colors.WHITE,
            fontSize: 13,
            opacity: 0.35,
            letterSpacing: 1.4,
          }}
        >
          {useCase.number}
        </span>
        <FoldHeaderText
          isDesktopSize={false}
          textStyles={{ fontSize: 22, letterSpacing: "-0.5px" }}
        >
          {useCase.title}
        </FoldHeaderText>
        <span style={{ color: Colors.GRAY1, fontSize: 14, lineHeight: "21px" }}>
          {useCase.subtext}
        </span>
        {learnMoreButton(() => learnMoreOnPress(useCase.docsSlug))}
      </div>
    </div>
  );

  const cardHeaderText = (useCase: UseCase, fontSize: number) => (
    <div className="ColumnNormal" style={{ position: "relative", gap: 14 }}>
      <span
        style={{
          color: Colors.WHITE,
          fontSize: 13,
          opacity: 0.35,
          letterSpacing: 1.4,
        }}
      >
        {useCase.number}
      </span>
      <FoldHeaderText
        isDesktopSize={isDesktopSize}
        textStyles={{ fontSize, letterSpacing: "-0.5px" }}
      >
        {useCase.title}
      </FoldHeaderText>
      <span style={{ color: Colors.GRAY1, fontSize: 16, lineHeight: "24px" }}>
        {useCase.subtext}
      </span>
      {learnMoreButton(() => learnMoreOnPress(useCase.docsSlug))}
    </div>
  );

  // Text and mockup pixel-positioned to match the Figma spec exactly, including the mockup
  // bleeding past the card's edge (clipped by overflow: hidden) (cards 01, 02).
  const renderAbsoluteCard = (useCase: UseCase) => {
    if (!isDesktopSize) {
      return (
        <div
          key={useCase.number}
          className="ColumnNormal"
          style={{
            position: "relative",
            width: "100%",
            gap: 20,
            padding: 28,
            borderRadius: 28,
            border: "1px solid rgba(255,255,255,0.1)",
            backgroundImage:
              useCase.background ??
              "linear-gradient(135deg, #141519 0%, #07080a 71.4%)",
            boxShadow: "0 16px 40px rgba(0,0,0,0.45)",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {cardGlow(460, 300, useCase.glowColor)}
          {cardHeaderText(useCase, 26)}
          <div
            className="slide-up"
            style={{
              position: "relative",
              width: "100%",
              maxWidth: 460,
              margin: "0 auto",
            }}
          >
            {useCase.mockup}
          </div>
        </div>
      );
    }

    const layout = useCase.absoluteLayout as AbsoluteLayout;
    return (
      <div
        key={useCase.number}
        style={{
          position: "relative",
          flex: useCase.rowShareFlex,
          height: useCase.cardHeight,
          borderRadius: 28,
          border: "1px solid rgba(255,255,255,0.1)",
          backgroundImage:
            useCase.background ??
            "linear-gradient(148.28deg, #141519 0%, #07080a 71.4%)",
          boxShadow: "0 16px 40px rgba(0,0,0,0.45)",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        {cardGlow(460, 300, useCase.glowColor)}
        <span
          style={{
            position: "absolute",
            left: layout.numberLeft,
            top: layout.numberTop,
            width: 40,
            color: Colors.WHITE,
            fontSize: 13,
            lineHeight: "16px",
            letterSpacing: 1.4,
            opacity: 0.35,
          }}
        >
          {useCase.number}
        </span>
        <div
          style={{
            position: "absolute",
            left: layout.titleLeft,
            top: layout.titleTop,
            width: layout.titleWidth,
          }}
        >
          <FoldHeaderText
            isDesktopSize
            textStyles={{
              fontSize: layout.titleFontSize,
              lineHeight: `${layout.titleLineHeight}px`,
              letterSpacing: layout.titleLetterSpacing,
            }}
          >
            {useCase.title}
          </FoldHeaderText>
        </div>
        <span
          style={{
            position: "absolute",
            left: layout.subtextLeft,
            top: layout.subtextTop,
            width: layout.subtextWidth,
            color: Colors.GRAY1,
            fontSize: layout.subtextFontSize,
            lineHeight: `${layout.subtextLineHeight}px`,
            letterSpacing: "-0.1px",
          }}
        >
          {useCase.subtext}
        </span>
        <div
          style={{
            position: "absolute",
            left: layout.buttonLeft,
            top: layout.buttonTop,
          }}
        >
          {learnMoreButton(() => learnMoreOnPress(useCase.docsSlug))}
        </div>
        <div
          className="slide-up"
          style={{
            position: "absolute",
            left: layout.mockupLeft,
            top: layout.mockupTop,
            width: layout.mockupWidth,
          }}
        >
          {useCase.mockup}
        </div>
      </div>
    );
  };

  // Text on the left, mockup pinned to the right (card 06)
  const renderSplitCard = (useCase: UseCase) => {
    if (!isDesktopSize) {
      return (
        <div
          key={useCase.number}
          className="ColumnNormal"
          style={{
            position: "relative",
            width: "100%",
            gap: 24,
            padding: 28,
            borderRadius: 28,
            border: "1px solid rgba(255,255,255,0.1)",
            backgroundImage:
              useCase.background ??
              "linear-gradient(135deg, #141519 0%, #07080a 71.4%)",
            boxShadow: "0 16px 40px rgba(0,0,0,0.45)",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {cardGlow(460, 300, useCase.glowColor)}
          {cardHeaderText(useCase, 32)}
          <div
            className="slide-up"
            style={{ position: "relative", width: "100%" }}
          >
            {useCase.mockup}
          </div>
        </div>
      );
    }

    return (
      <div
        key={useCase.number}
        style={{
          position: "relative",
          width: "100%",
          height: useCase.cardHeight,
          borderRadius: 28,
          border: "1px solid rgba(255,255,255,0.1)",
          backgroundImage:
            useCase.background ??
            "linear-gradient(135deg, #141519 0%, #07080a 71.4%)",
          boxShadow: "0 16px 40px rgba(0,0,0,0.45)",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        {cardGlow(760, 380, useCase.glowColor)}
        <div style={{ position: "absolute", left: 47, top: 74, width: 430 }}>
          {cardHeaderText(useCase, 32)}
        </div>
        <div
          className="slide-up"
          style={{ position: "absolute", left: 595, top: 11 }}
        >
          {useCase.mockup}
        </div>
      </div>
    );
  };

  const renderUseCase = (useCase: UseCase) => {
    if (useCase.layout === "scrim") return renderScrimCard(useCase);
    if (useCase.layout === "absolute") return renderAbsoluteCard(useCase);
    return renderSplitCard(useCase);
  };

  const dropdownItems: DropdownMenuItem[] = useCases.map((useCase, index) => ({
    title: useCase.title,
    key: useCase.number,
    onPress: () => setSelectedUseCase(index),
  }));

  return (
    <div
      className={isDesktopSize ? "ColumnCentering" : "ColumnNormal"}
      style={{
        gap: isDesktopSize ? 60 : 50,
        width: "100%",
        backgroundImage: "linear-gradient(to bottom, #141516 0%, #000000 31%)",
        paddingBlock: isDesktopSize ? 80 : 60,
      }}
    >
      <div
        className={isDesktopSize ? "ColumnCentering" : "ColumnNormal"}
        style={{
          gap: 24,
          maxWidth: isDesktopSize ? 722 : "100%",
          paddingInline: 20,
        }}
      >
        <div
          className={isDesktopSize ? "ColumnCentering" : "ColumnNormal"}
          style={{ gap: 10 }}
        >
          <div className="AlignedRow" style={{ gap: 7 }}>
            <Icon
              icon="ion:wallet"
              height={14}
              style={{ color: Colors.PINK2 }}
            />
            <span style={{ ...styles.headerLabel, color: Colors.PINK2 }}>
              Monetize your Texts
            </span>
          </div>
          <FoldHeaderText
            isDesktopSize={isDesktopSize}
            textStyles={{ textAlign: isDesktopSize ? "center" : "left" }}
          >
            More than just texting
          </FoldHeaderText>
        </div>
        <span
          className="landingPageHeaderSubtext"
          style={{ textAlign: isDesktopSize ? "center" : "left" }}
        >
          Forget all of your other tools, Markit is the only platform
          you&apos;ll need.
        </span>
      </div>
      {isDesktopSize ? (
        <div
          className="ColumnNormal slide-up"
          style={{ gap: 20, width: "100%", maxWidth: 1152 }}
        >
          {/* Row A */}
          <div
            className="AlignedRow"
            style={{ gap: 20, alignItems: "flex-start" }}
          >
            {renderUseCase(useCases[0])}
            {renderUseCase(useCases[1])}
          </div>
          {/* Row B */}
          <div
            className="AlignedRow"
            style={{ gap: 20, alignItems: "flex-start" }}
          >
            {renderUseCase(useCases[2])}
            {renderUseCase(useCases[3])}
            {renderUseCase(useCases[4])}
          </div>
          {/* Row C */}
          {renderUseCase(useCases[5])}
        </div>
      ) : (
        <div
          className="ColumnNormal slide-up"
          style={{ gap: 20, width: "100%" }}
        >
          <div className="AlignedRowSpaced" style={{ paddingInline: 20 }}>
            <FoldHeaderText
              isDesktopSize={false}
              textStyles={{ fontSize: 24, letterSpacing: "-1px" }}
            >
              Use cases
            </FoldHeaderText>
            <DropdownMenu
              dropdownItems={dropdownItems}
              dark
              trigger={["click"]}
              placement="bottomRight"
            >
              <div
                className="AlignedRow"
                style={{
                  gap: 10,
                  padding: "10px 14px",
                  borderRadius: 8,
                  border: `1px solid ${Colors.GRAY3}80`,
                  backgroundColor: Colors.GRAY10,
                }}
              >
                <span style={{ color: Colors.WHITE, fontSize: 14 }}>
                  {useCases[selectedUseCase].title}
                </span>
                <Icon
                  icon="ion:chevron-down"
                  height={10}
                  style={{ color: Colors.WHITE }}
                />
              </div>
            </DropdownMenu>
          </div>
          <div style={{ paddingInline: 20 }}>
            {renderUseCase(useCases[selectedUseCase])}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(MoreThanJustTextingFold);
